import React from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { UserStatusSelector } from '../user-status-selector/user-status-selector';
import type { User } from '../../../models/user';
import { Status } from '../../../features/auth/auth.slice';
import { PasswordTextField, SelectTextField, TextField } from '../../TextField';
import { AutocompleteTextField } from '../../AutocompleteTextField';

const useStyles = makeStyles({
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '4px',
  },
  fieldContainer: {
    margin: 0,
    '& .MuiFormControl-root': {
      margin: 0,
    },
    '& .MuiTextField-root': {
      margin: 0,
    },
  },
});

interface Field {
  required: boolean;
  error?: boolean;
  disabled?: boolean;
}

interface Props {
  user: User & { password?: string };
  updateUser: (user: User) => void;
  fields: {
    name?: Field;
    email?: Field;
    contactNumber?: Field;
    status?: Field;
    parentId?: Field & { managedByList?: any[] };
    password?: Field;
    role?: Field & { options: { label: string; value: string }[] };
  };
}

export const UserForm: React.FunctionComponent<Props> = ({
  user,
  updateUser,
  fields,
}) => {
  const intl = useIntl();
  const classes = useStyles();

  // TODO: Refactor onChangeHandlers

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedUser = { ...user };
    if (name === 'email') {
      updatedUser.email = value;
    } else {
      updatedUser.profile = {
        ...user.profile,
        [name]: value,
      };
    }
    updateUser(updatedUser);
  };

  const onAutocompleteChange = (_e, value: any) => {
    const updatedUser = { ...user };
    updatedUser.parentId = value;
    updateUser(updatedUser);
  };

  const passwordOnChange = (e: { target: { value: string } }) => {
    const updatedUser = { ...user };
    updatedUser.password = e.target.value;
    updateUser(updatedUser);
  };

  const _roleOnChange = (e: any) => {
    const updatedUser = { ...user };
    updatedUser.role = e.target.value;
    updateUser(updatedUser);
  };

  return (
    <div className={classes.formContainer}>
      {fields.name && (
        <TextField
          autoFocus
          id="user-name"
          value={user?.profile?.name}
          error={fields.name.error}
          placeholder={intl.formatMessage({
            id: 'name',
          })}
          label={intl.formatMessage({
            id: 'name',
          })}
          name="name"
          onChange={onChange}
          required={fields.name.required}
          helperText={
            fields.name.error &&
            intl.formatMessage({
              id: 'please_insert_valid_name',
            })
          }
          customStyles={classes.fieldContainer}
          disabled={fields.name.disabled}
        />
      )}
      {fields.contactNumber && (
        <TextField
          id="user-number"
          value={user?.profile?.contactNumber}
          error={fields.contactNumber.error}
          placeholder={intl.formatMessage({
            id: 'contact_number',
          })}
          label={intl.formatMessage({
            id: 'contact_number',
          })}
          name="contactNumber"
          type="tel"
          onChange={onChange}
          required={fields.contactNumber.required}
          helperText={
            fields.contactNumber.error &&
            intl.formatMessage({
              id: 'please_insert_valid_contact_number',
            })
          }
          disabled={fields.name.disabled}
          customStyles={classes.fieldContainer}
        />
      )}
      {fields.email && (
        <TextField
          id="user-email"
          value={user.email}
          error={fields.email.error}
          placeholder={intl.formatMessage({
            id: 'email',
          })}
          label={intl.formatMessage({
            id: 'email',
          })}
          onChange={onChange}
          required={fields.email.required}
          name="email"
          helperText={
            fields.email.error &&
            intl.formatMessage({
              id: 'please_insert_valid_email',
            })
          }
          disabled={fields.email.disabled}
          customStyles={classes.fieldContainer}
        />
      )}
      {fields.status && (
        <UserStatusSelector
          onChange={(status: string) => {
            const updatedUser: User = { ...user, status: status as Status };
            updateUser(updatedUser);
          }}
          value={user.status || ''}
          disabled={fields.status.disabled}
        />
      )}
      {fields.parentId && (
        <AutocompleteTextField
          multiple={false}
          limitTags={1}
          id="select-managed-by-dropdown"
          options={fields.parentId.managedByList || []}
          getOptionLabel={(option) => getManagedByWithPrefix(option)}
          value={user.parentId || ''}
          onChange={onAutocompleteChange}
          label={intl.formatMessage({
            id: 'managed_by',
          })}
          placeholder={intl.formatMessage({
            id: 'managed_by',
          })}
          name="parentId"
          required={fields.parentId.required}
          error={fields.parentId.error}
          helperText={
            fields.parentId.error &&
            intl.formatMessage({
              id: 'please_insert_valid_managed_by',
            })
          }
          disabled={fields.parentId.disabled}
          customStyles={classes.fieldContainer}
        />
      )}
      {fields.password && (
        <PasswordTextField
          id="add-user-password"
          placeholder={intl.formatMessage({
            id: 'password',
          })}
          required={fields.password.required}
          onChange={passwordOnChange}
          error={fields.password.error}
          helperText={
            fields.password.error &&
            intl.formatMessage({
              id: 'please_insert_valid_password',
            })
          }
          disabled={fields.password.disabled}
          customClasses={classes.fieldContainer}
        />
      )}
      {fields.role && (
        <SelectTextField
          selectItems={fields.role.options || []}
          value={user.role || ''}
          label={intl.formatMessage({
            id: 'role',
          })}
          onChange={_roleOnChange}
          name="role"
          disabled={fields.role.disabled}
        />
      )}
    </div>
  );
};

const getManagedByWithPrefix = (option) => {
  if (option?.role === 'subadmin') return `ABU: ${option?.profile?.name || ''}`;
  if (option?.role === 'customer') return `AU: ${option?.profile?.name || ''}`;
  return '';
};
